import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'
import { useRef, useEffect } from 'react'

import { useLassoDrawing } from '@aytm/app/stats/page/question-views/hm/components/chart/components/lasso/hooks/useLassoDrawing'
import { useLassoKeyboard } from '@aytm/app/stats/page/question-views/hm/components/chart/components/lasso/hooks/useLassoKeyboard'
import { useLassoTooltip } from '@aytm/app/stats/page/question-views/hm/components/chart/components/lasso/hooks/useLassoTooltip'
import {
  borderToSvgPoints,
  calcLassoData
} from '@aytm/app/stats/page/question-views/hm/components/chart/components/lasso/lib/utils'
import useImageScale from '@aytm/app/stats/page/question-views/hm/components/chart/hooks/useImageScale'

import style from '@aytm/app/stats/page/question-views/hm/components/chart/components/lasso/styles/Lasso.module.scss'

const HANDLE_RADIUS = 4

const Lasso = ({ cluster, image, containerWidth, answer, onLassoChange, onTipChange }) => {
  const {
    imageHeight: imageH,
    scale,
    scaledWidth: scaledW,
    scaledHeight: scaledH
  } = useImageScale(image, containerWidth)

  const color = cluster?.color
  const initialPoints = cluster?.border?.length > 0 ? borderToSvgPoints(cluster.border, imageH, scale) : []

  const {
    points,
    mousePos,
    drawing,
    closed,
    isDragging,
    svgRef,
    handleSvgMouseUp,
    handleContextMenu,
    handleSvgMouseMove,
    handleCircleMouseDown,
    removeLastDot
  } = useLassoDrawing({ initialPoints })

  const { tooltip, showTooltip, hideTooltip } = useLassoTooltip({ answer, imageH, scale })

  useLassoKeyboard({ onRemoveLastDot: removeLastDot, enabled: drawing && !closed })

  const propsRef = useRef({ scale, imageH, answer })
  propsRef.current = { scale, imageH, answer }

  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }

    if (points.length === 0) return

    const { scale, imageH, answer } = propsRef.current

    onTipChange?.(3)
    onLassoChange?.(calcLassoData(points, scale, imageH, answer))
  }, [closed])

  useEffect(() => {
    if (closed) return

    onTipChange?.(points.length >= 3 ? 2 : 1)
  }, [closed, points.length])

  const prevIsDraggingRef = useRef(false)
  useEffect(() => {
    const was = prevIsDraggingRef.current
    prevIsDraggingRef.current = isDragging

    if (was && !isDragging && points.length > 0) {
      const { scale, imageH, answer } = propsRef.current
      onLassoChange?.(calcLassoData(points, scale, imageH, answer))
    }

    if (!isDragging) hideTooltip()
  }, [isDragging])

  useEffect(() => {
    if (isDragging && mousePos && points.length > 0) {
      showTooltip(mousePos, points)
    }
  }, [mousePos])

  const handleMouseMoveWithTooltip = e => {
    handleSvgMouseMove(e)
    if (drawing && mousePos && points.length >= 2) {
      showTooltip(mousePos, [...points, mousePos])
    }
  }

  const pointsAttr = points.map(p => p.join(',')).join(' ')
  const previewPoints = mousePos && drawing ? [...points, mousePos] : points
  const previewPointsAttr = previewPoints.map(p => p.join(',')).join(' ')
  const lastPoint = points[points.length - 1]

  return (
    <div
      className={classNames(style.lasso, {
        [style.drawing]: drawing && !closed,
        [style.drawn]: closed
      })}
    >
      <svg
        ref={svgRef}
        width={scaledW}
        height={scaledH}
        viewBox={`0 0 ${scaledW} ${scaledH}`}
        className={style.svg}
        onMouseUp={handleSvgMouseUp}
        onMouseMove={handleMouseMoveWithTooltip}
        onMouseLeave={hideTooltip}
        onContextMenu={handleContextMenu}
      >
        {drawing && !closed && (
          <g>
            {previewPoints.length > 2 && (
              <polygon points={previewPointsAttr} fill={color} fillOpacity={0.2} stroke="none" />
            )}

            {points.length > 1 && <polyline points={pointsAttr} fill="none" stroke={color} strokeWidth={4} />}

            {mousePos && lastPoint && (
              <line
                x1={lastPoint[0]}
                y1={lastPoint[1]}
                x2={mousePos[0]}
                y2={mousePos[1]}
                stroke={color}
                strokeWidth={4}
              />
            )}

            {points.map((pt, i) => (
              <circle
                key={i}
                cx={pt[0]}
                cy={pt[1]}
                r={HANDLE_RADIUS}
                fill="white"
                stroke="#000"
                strokeWidth={1}
                data-is-handle="true"
                style={{ cursor: i === 0 && points.length > 2 ? 'pointer' : 'default' }}
              />
            ))}
          </g>
        )}

        {closed && (
          <g>
            <polygon points={pointsAttr} fill={color} fillOpacity={0.2} stroke={color} strokeWidth={4} />

            {points.map((pt, i) => (
              <circle
                key={i}
                cx={pt[0]}
                cy={pt[1]}
                r={HANDLE_RADIUS}
                fill="#FDBC07"
                stroke="#000"
                strokeWidth={1}
                style={{ cursor: 'move' }}
                onMouseDown={e => handleCircleMouseDown(i, e)}
              />
            ))}
          </g>
        )}
      </svg>

      {tooltip && (
        <div className={style.tooltip} style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.items.map(({ value, label }, i) => (
            <div key={i} className={style.tooltipLine}>
              {value !== null && <strong>{value}</strong>} {label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

Lasso.displayName = 'HeatmapLasso'

Lasso.propTypes = {
  cluster: PropTypes.shape({
    color: PropTypes.string,
    border: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  }),
  image: PropTypes.shape({
    original: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number
    })
  }),
  containerWidth: PropTypes.number,
  answer: answerPropType,
  onLassoChange: PropTypes.func,
  onTipChange: PropTypes.func
}

Lasso.defaultProps = {
  containerWidth: 0
}

export default Lasso
